// Declarative section config for the V2 request edit layout.
// Each entry is consumed by:
//   - RequestSectionNav  (left rail — renders the label/icon, applies
//     active/done/locked/missing visual state)
//   - RequestSectionView (middle pane — renders the corresponding
//     molecule)
//   - RequestSmartCard   (right rail — runs getCardData against live
//     context to produce the block contents)
//
// Adding a new section means adding one entry here, nothing else.

import { symbolFor, baseSymbol, formatMoneyMajor } from "../../core/currency";

export const SECTION_IDS = {
  SEARCH_PRODUCT: "search_product",
  ASSIGN_CLIENT: "assign_client",
  CONFIRM_REQUEST: "confirm_request",
  CONNECT_LISTING: "connect_listing",
  SEND_QUOTE: "send_quote",
  // Order / PX-only
  ASSIGN_SELL_REQUESTS: "assign_sell_requests",
  PX_SUMMARY: "px_summary",
  // Sell-only
  VERIFY_SERIAL: "verify_serial",
  CONSIGNMENT_AGREEMENT: "consignment_agreement",
  PURCHASING_AGREEMENT: "purchasing_agreement",
  SELL_PAYMENTS: "sell_payments",
  PURCHASE_ORDER: "purchase_order",
  PUBLISH_LISTING: "publish_listing",
  // Order / PX cart sections
  PAYMENTS: "payments",
  INVOICE: "invoice",
  COMMISSIONS: "commissions",
  INTERNAL_DETAILS: "internal_details",
  TRACKING_NUMBER: "tracking_number",
};

// Stable display order. Visibility per request type is controlled by
// isVisible(state). Sell-only sections appear after SEND_QUOTE in the
// natural workflow order.
//
// Two sections are intentionally removed in the multi-request-group
// rollout:
//   - CONNECT_LISTING: linking a Listing to a Request now happens
//     either at SEARCH PRODUCT (rep picks "From active listing"
//     source, which writes request.linked_listing_id directly) OR at
//     SEND_QUOTE quote approval (QuoteService::create writes
//     listing.linked_order_request_id + listing.linked_order_quote_id).
//   - ASSIGN_SELL_REQUESTS: assigning existing sell requests as PX
//     trade-ins now happens through the SELECT SELL SOURCE chooser
//     "Existing sell request" tab in the dynamic SELECT SELL REQUEST
//     (m/n) sections. The standalone section is no longer needed.
//
// Their SECTION_IDS entries remain (callers reference them) but
// they're removed from SECTION_ORDER + SECTIONS so getVisibleSections
// returns nothing for them.
export const SECTION_ORDER = [
  SECTION_IDS.SEARCH_PRODUCT,
  SECTION_IDS.CONNECT_LISTING,
  SECTION_IDS.ASSIGN_CLIENT,
  SECTION_IDS.CONFIRM_REQUEST,
  SECTION_IDS.SEND_QUOTE,
  // Sell
  SECTION_IDS.VERIFY_SERIAL,
  SECTION_IDS.CONSIGNMENT_AGREEMENT,
  SECTION_IDS.PURCHASING_AGREEMENT,
  SECTION_IDS.SELL_PAYMENTS,
  SECTION_IDS.PURCHASE_ORDER,
  SECTION_IDS.PUBLISH_LISTING,
  // Order / PX
  SECTION_IDS.PX_SUMMARY,
  // INTERNAL DETAILS & COSTS now comes BEFORE payments, not after the invoice
  // (Alex, 2026-09-04). The reason is the serial number: it is captured here,
  // and the invoice's line description reads it — so filling this in after the
  // invoice had already been generated meant every deal produced an invoice
  // with no serial on it and then needed a regeneration to fix.
  //
  // Nothing about the cart Order changed. It is still created by saving
  // PAYMENTS. This section never needed the Order: the fields it writes
  // (serial, buying price, supplier, the cost rows) are saved onto the
  // LISTING — `saveCosts` → `saveListing` → the listing endpoint — and
  // `seededListings` in RequestTemplateV2 already resolves "the Order's items
  // if the Order exists, OTHERWISE the quote's linked listing". So the rows it
  // renders from are there as soon as a quote has a listing. It was only its
  // own `isVisible` gate, which asked for an invoice number, that kept it at
  // the end.
  SECTION_IDS.INTERNAL_DETAILS,
  SECTION_IDS.PAYMENTS,
  SECTION_IDS.INVOICE,
  SECTION_IDS.COMMISSIONS,
  SECTION_IDS.TRACKING_NUMBER,
];

const isSellRequest = (state) => state?.requestType === "sell";

// A sell request the backend is already holding.
//
// The five paperwork sections below (agreements, payments, purchase order,
// publish) are all operations ON a saved request, so showing them before one
// exists put eight rows and "0 of 8 steps done" in front of a rep who had not
// typed anything yet. The ORDER flow reads "0 of 3" at the same moment, not
// because it has a step list but because ITS later sections gate on data that
// does not exist yet — request.id, then a linked listing, then order.id. This
// is the sell side using the same gate (Alex, 2026-09-24).
//
// Only the unsaved flow changes: any request opened from a list or a link has
// an id, so its rail is exactly what it was.
const isSavedSellRequest = (state) => isSellRequest(state) && !!state.request?.id;
const isOrderOrPx = (state) =>
  ["order", "px-order", "px-cart"].includes(state?.requestType);

// A main ORDER slot picked from ACTIVE LISTINGS already carries its price from
// that listing, so there is nothing to quote and the slot gets no SELECT ORDER
// QUOTE section.
//
// `linked_listing_id` is exactly that signal, and the narrow one on purpose:
// V2SearchProduct's Active-Listings path stamps it on the slot's own request
// (handleSelectListing), and the backend's detach-listing endpoint documents it
// as the Active-Listings pointer — as distinct from `quotes.listing_id`, which is
// the Listings LINKER (a QUOTED deal that later had a listing attached, and which
// must keep its quote section so the rep can still change that quote).
// Deliberately NOT `state.listing`: a px-order overlay sets that from
// order.items[0].listing for any cart listing, including one linked long after a
// Database-sourced request was quoted.
//
// slotIdx 0 = the primary request; 1..2 = additionalMainRequests[slotIdx - 1].
//
// ORDER side only. Sell requests have no Active Listings source; their
// linked_listing_id means something else (see isIntakeSellRequest), and reading
// it here hid SELECT QUOTE on sell requests that had a quote to change
// (/request/sell/9355, 2026-10-28).
const isMainSlotListingSourced = (state, slotIdx = 0) =>
  !isOrderOrPx(state)
    ? false
    : slotIdx === 0
    ? !!state?.request?.linked_listing_id
    : !!state?.additionalMainRequests?.[slotIdx - 1]?.linked_listing_id;

// A sell request gets linked_listing_id from the listing side: New Stock / New
// Consignment create it already 'done' with no quote (createDoneSellRequest),
// and the listing edit screen / supplier slot attach an existing one. Only the
// first kind has nothing to quote — the listing's buying price is the price.
// An attached request that is still open, or one that already has a quote,
// keeps SELECT QUOTE so the rep can set or change it.
const isIntakeSellRequest = (state) =>
  isSellRequest(state) &&
  !!state?.request?.linked_listing_id &&
  !state?.request?.last_quote?.id &&
  state?.request?.status === "done";

// Order-side quote sections read "Select Order Quote", so they cannot be
// confused with the sell side's "Select Sell Quote". The standalone sell flow
// keeps its plain "Select Quote".
const orderQuoteLabel = (state) =>
  isOrderOrPx(state) ? "Select Order Quote" : "Select Quote";

// How many order-quote sections the deal will actually show. Used to decide
// whether a slot counter belongs on the label at all: with only ONE quote in the
// deal, "Select Order Quote (1/2)" invites the rep to look for a (2/2) that does
// not exist — the 2nd product was sourced from Active Listings and needs no quote.
// When two or more DO exist the counter stays slot-based, matching the
// SEARCH/CONFIRM ORDER PRODUCT labels for the same slot.
const orderQuoteSlotCount = (state) => {
  if (!state?.user?.is_admin) return 0;
  const additional = state?.additionalMainProducts ?? [];
  let n = state?.request?.id && !isMainSlotListingSourced(state, 0) ? 1 : 0;
  for (let i = 0; i < additional.length; i++) {
    if (!isMainSlotListingSourced(state, i + 1)) n += 1;
  }
  return n;
};

// Staging has no live Xero connection, so order.xero_invoice_number
// never gets populated and the post-Invoice sections (COMMISSIONS etc.)
// stay invisible. On staging only, treat the Invoice gate as satisfied
// once the cart Order exists so reps can walk the rest of the flow.
// Production keeps the original Xero-backed gate untouched.
const isStagingEnv = () => {
  if (import.meta.env.DEV) return true;
  const apiUrl = import.meta.env.VITE_API_URL || "";
  return apiUrl.toLowerCase().includes("staging");
};

// "Serial has been verified" — preferred signal is the is_verified flag
// on the Request, but the backend's clientVerify endpoint historically
// stamped the serial number on the listing without flipping the flag.
// Falling back to serial_number presence keeps the downstream sections
// (Purchasing/Consignment Agreement, Purchase Order) unlocked for sell
// requests where the rep has completed the Verify Serial step but the
// flag was never set. Once the backend fix lands, the flag is the
// authoritative signal; the fallback stays as a safety net for
// historical records.
const isSerialVerified = (request) =>
  !!(request?.is_verified || request?.serial_number);

// A PO exists — issued in Xero (xero_purchase_order_id) or as our own PDF for
// a store without Xero on its plan (purchase_order_number).
const hasPurchaseOrder = (request) =>
  !!(request?.xero_purchase_order_id || request?.purchase_order_number);
const PO_FEATURES = "xero_purchase_orders|purchase_order_generation";

// Same for the order invoice: xero_invoice_number, or invoice_number when it
// was generated natively.
const hasInvoice = (order) =>
  !!(order?.xero_invoice_number || order?.invoice_number);

// A client counts as assigned if any of the three places it can land says so:
// local `client` state (set by assignClient before the request round-trips),
// the embedded `request.client`, or the bare `request.client_id`. Shared by
// ASSIGN_CLIENT's isComplete and CONFIRM_REQUEST's isLocked so the nav tick and
// the gate can never disagree.
const hasClient = ({ request, client } = {}) =>
  !!(client?.id || request?.client?.id || request?.client_id);

// Each section gets:
//   id            — stable id from SECTION_IDS
//   label         — display label in the left rail
//   icon          — icon key consumed by SectionTrigger
//   moleculeKey   — which molecule renders in the middle pane (resolved
//                   by RequestSectionView's component map)
//   isVisible     — predicate against {requestType, user, order} that
//                   controls whether the section appears for this flow
//   isComplete    — heuristic for "done" state in the nav and card
//   isLocked      — section is reachable in principle but a prerequisite
//                   isn't met yet (returns a string message or false)
//   getCardData   — produces the smart-card block for this section
export const SECTIONS = {
  [SECTION_IDS.SEARCH_PRODUCT]: {
    id: SECTION_IDS.SEARCH_PRODUCT,
    label: "Search Product",
    icon: "search",
    moleculeKey: "SearchProduct",
    isVisible: () => true,
    isComplete: ({ request, product }) => !!(product?.id ?? request?.product_id),
    isLocked: () => false,
    getCardData: ({ request, product }) => {
      const p = product || request?.product;
      if (!p?.id) return { fields: [] };
      return {
        image: p.images?.[0]?.id ?? null,
        fields: [
          { label: "Product ID", value: p.id },
          { label: "Brand", value: p.brand?.name ?? "—" },
          { label: "Model", value: p.model?.name ?? "—" },
          { label: "Reference", value: p.reference_number ?? "—" },
        ],
      };
    },
  },
  [SECTION_IDS.ASSIGN_CLIENT]: {
    id: SECTION_IDS.ASSIGN_CLIENT,
    label: "Assign Client",
    icon: "client",
    moleculeKey: "AssignClient",
    isVisible: ({ user }) => !!user?.is_admin,
    // Honor the local `client` state (set by assignClient) in addition
    // to request.client — the PUT response may include client_id
    // without an embedded client object, leaving request.client?.id
    // undefined and the FILL IN pill stuck even after a successful
    // selection.
    isComplete: hasClient,
    isLocked: () => false,
    getCardData: ({ request, client }) => {
      const c = client || request?.client;
      if (!c?.id) return { fields: [] };
      return {
        fields: [
          { label: "Client", value: c.full_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—" },
          { label: "User ID", value: c.id },
        ],
      };
    },
  },
  [SECTION_IDS.CONFIRM_REQUEST]: {
    id: SECTION_IDS.CONFIRM_REQUEST,
    label: "Confirm Request",
    icon: "confirm",
    moleculeKey: "ConfirmRequest",
    isVisible: () => true,
    isComplete: ({ request }) => !!request?.id,
    // Same predicate as ASSIGN_CLIENT's isComplete — reading only
    // `request.client_id` here left the step locked while the nav already
    // ticked Assign Client (record-sale assigns into local `client` state and
    // the PUT response need not echo client_id back).
    isLocked: (state) => (hasClient(state) ? false : "Assign a client first"),
    // Dead code — nothing consumes getCardData any more (RequestSmartCard
    // renders its own rows). The old block here also formatted budgets with
    // the base symbol on the false premise that requests carry no currency
    // field; budgets render in request.currency on the card itself now.
    getCardData: () => ({ fields: [] }),
  },
  // LISTINGS — the canonical "what listings are attached to this order"
  // section. Two states:
  //   1. No listing linked yet → renders the linker UI (create from
  //      quote OR pick existing). This is the escape hatch that
  //      unblocks Payments for orders created from the catalog.
  //   2. Listing linked → renders summary cards with a remove button.
  //      Removal is blocked when a deposit exists against the listing.
  // Always visible for orders once a quote exists, so the user always
  // has a clear surface for the listing<>order relationship.
  [SECTION_IDS.CONNECT_LISTING]: {
    id: SECTION_IDS.CONNECT_LISTING,
    label: "Shopping Cart",
    icon: "link",
    moleculeKey: "ConnectListing",
    isVisible: (state) => {
      if (!isOrderOrPx(state)) return false;
      const r = state?.request;
      // Visible if we have a quote (linker can write quote.listing_id)
      // OR if a listing is already linked via the Active Listings tab
      // path (request.linked_listing_id, set without needing a quote).
      return !!r?.last_quote?.id || !!r?.linked_listing_id;
    },
    isComplete: ({ request }) =>
      !!(request?.last_quote?.listing_id || request?.linked_listing_id),
    isLocked: ({ request }) => {
      if (request?.last_quote?.id) return false;
      if (request?.linked_listing_id) return false;
      return "Create a quote first";
    },
    getCardData: ({ request, listings }) => {
      const listingId =
        request?.last_quote?.listing_id ?? request?.linked_listing_id;
      if (!listingId) return { fields: [] };
      const linked = (listings ?? []).find((l) => l.id === listingId);
      const fields = [{ label: "Listing ID", value: listingId }];
      if (linked?.buying_price) {
        fields.push({
          label: "Buying Price",
          value: `${(linked.buying_currency || "gbp").toUpperCase()} ${(linked.buying_price / 100).toLocaleString()}`,
        });
      }
      if (linked?.supplier) {
        fields.push({ label: "Supplier", value: linked.supplier });
      }
      return { fields };
    },
  },
  [SECTION_IDS.SEND_QUOTE]: {
    id: SECTION_IDS.SEND_QUOTE,
    label: "Select Quote",
    icon: "sendQuote",
    moleculeKey: "SendQuote",
    isVisible: (state) =>
      !!state?.user?.is_admin &&
      !!state?.request?.id &&
      // Nothing to quote when the product came from Active Listings — that
      // listing IS the price. Keeps PAYMENTS and the rest untouched: they gate
      // on a linked listing, which this slot has by definition.
      !isMainSlotListingSourced(state, 0) &&
      !isIntakeSellRequest(state),
    isComplete: (state) =>
      !!state?.request?.last_quote?.quote_send_type ||
      // The listing pointers stand in for a quote on the ORDER side only (see
      // isMainSlotListingSourced); a sell request still needs its quote.
      (isOrderOrPx(state) &&
        (!!state?.request?.last_quote?.listing_id ||
          !!state?.request?.linked_listing_id)),
    isLocked: ({ request }) =>
      request?.id ? false : "Confirm the request first",
    getCardData: ({ request, listings }) => {
      const q = request?.last_quote;
      if (!q?.id) return { fields: [] };
      const linkedListing = (listings ?? []).find(
        (l) => l.id === q.listing_id
      );
      // If admin skips SEND QUOTE entirely, the linked listing's
      // price acts as the quoted price downstream — surface that
      // here so the smart card matches what the cart flow will use.
      const listingPriceCents =
        linkedListing?.listing_price ??
        linkedListing?.price ??
        linkedListing?.sell_price ??
        null;
      const quotedPriceCents = q.price ?? listingPriceCents;
      const quotedPriceFromListing = !q.price && listingPriceCents != null;
      // Symbol follows whichever record supplied the price: the quote's
      // own currency, or the linked listing's when we fell back to it.
      // Null currency = legacy GBP record.
      const quotedPriceCurrency =
        (quotedPriceFromListing ? linkedListing?.currency : q.currency) ??
        "gbp";
      return {
        fields: [
          {
            label: "Quoted Price",
            value:
              quotedPriceCents != null
                ? `${symbolFor(quotedPriceCurrency)}${(quotedPriceCents / 100).toLocaleString()}`
                : "—",
            note: quotedPriceFromListing ? "From linked listing" : undefined,
          },
          { label: "Buying Price", value: linkedListing?.buying_price
              ? `${linkedListing.buying_currency?.toUpperCase() || ""} ${(linkedListing.buying_price / 100).toLocaleString()}`
              : "—" },
          { label: "Supplier", value: linkedListing?.supplier ?? "—" },
        ],
      };
    },
  },
  [SECTION_IDS.VERIFY_SERIAL]: {
    id: SECTION_IDS.VERIFY_SERIAL,
    label: "Verify Serial",
    icon: "serial",
    moleculeKey: "VerifySerial",
    isVisible: isSavedSellRequest,
    isComplete: ({ request }) => !!request?.serial_number,
    isLocked: ({ request }) =>
      request?.last_quote?.id ? false : "Send a quote first",
    getCardData: ({ request }) => ({
      fields: request?.serial_number
        ? [{ label: "Serial", value: request.serial_number }]
        : [],
    }),
  },
  [SECTION_IDS.CONSIGNMENT_AGREEMENT]: {
    id: SECTION_IDS.CONSIGNMENT_AGREEMENT,
    label: "Consignment Agreement",
    icon: "doc",
    moleculeKey: "ConsignmentAgreement",
    // Purchasing / consignment agreements (Feature 43).
    feature: "purchase_consignment_agreements",
    // Always visible for sell requests. The Xero PO gate that used
    // to live here meant agreements only appeared AFTER PURCHASE
    // ORDER was generated — but reps need to prep the paperwork
    // before the PO step, so the section unlocks on serial verify.
    isVisible: isSavedSellRequest,
    // Done once the backend has recorded the agreement send. The
    // column is back-filled on every POST /send-consignment-agreement
    // call, so historic requests without the timestamp continue to
    // show as pending until the next send.
    isComplete: ({ request }) => !!request?.consignment_agreement_sent_at,
    isLocked: ({ request }) =>
      isSerialVerified(request) ? false : "Verify the serial first",
    getCardData: () => ({ fields: [] }),
  },
  [SECTION_IDS.PURCHASING_AGREEMENT]: {
    id: SECTION_IDS.PURCHASING_AGREEMENT,
    label: "Purchasing Agreement",
    icon: "doc",
    moleculeKey: "PurchasingAgreement",
    feature: "purchase_consignment_agreements",
    isVisible: isSavedSellRequest,
    isComplete: ({ request }) => !!request?.purchasing_agreement_sent_at,
    isLocked: ({ request }) =>
      isSerialVerified(request) ? false : "Verify the serial first",
    getCardData: () => ({ fields: [] }),
  },
  [SECTION_IDS.SELL_PAYMENTS]: {
    id: SECTION_IDS.SELL_PAYMENTS,
    label: "Payments",
    icon: "payments",
    moleculeKey: "SellPayments",
    isVisible: isSavedSellRequest,
    // Done when the seller has been fully paid: target = sell quote price,
    // paid = sum of deposit rows + the Total Remaining Paid row.
    //
    // Unit gotcha: on sell requests the deposit / remaining amounts
    // are stored in raw GBP (the form value passes through
    // /api/requests/{id}/add-price unchanged — StepPayments only
    // multiplies by 100 at the very end of its own paidAmount calc).
    // last_quote.price is in pence. Multiply the paid sum by 100
    // before comparing so a £9,900 final payment (amount="9900")
    // actually satisfies a £9,900 quote (price=990000).
    isComplete: ({ request }) => {
      const target = Number(request?.last_quote?.price ?? 0);
      if (target <= 0) return false;
      const depositsPaidRaw = (request?.deposits?.deposits || [])
        .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
      const remainingPaidRaw = Number(
        request?.deposits?.total_remaining_paid?.amount ?? 0
      );
      return ((depositsPaidRaw + remainingPaidRaw) * 100) >= target;
    },
    isLocked: ({ request }) =>
      request?.last_quote?.id ? false : "Send a quote first",
    // Each row renders with ITS OWN stored currency (null ⇒ legacy GBP, never
    // the deal's) through the shared formatter — the ad-hoc
    // `CODE ${amount / 100}` string it replaces was wrong twice over: the sell
    // blob holds MAJOR units (see isComplete above, which multiplies by 100),
    // so dividing by 100 showed a £5,000 deposit as "GBP 50"; and `?? "gbp"`
    // let an empty-string currency through as "".
    getCardData: ({ request }) => {
      const fields = [];
      const rowCurrency = (row) =>
        String(row?.currency || "").toLowerCase() || "gbp";
      const deposits = request?.deposits?.deposits ?? [];
      deposits.forEach((d, i) => {
        if (!Number(d.amount)) return;
        fields.push({
          label: `Deposit ${i + 1}`,
          value: formatMoneyMajor(d.amount, rowCurrency(d)),
        });
      });
      const remaining = request?.deposits?.total_remaining_paid;
      if (remaining?.amount > 0) {
        fields.push({
          label: "Final Payment",
          value: formatMoneyMajor(remaining.amount, rowCurrency(remaining)),
        });
      }
      return { fields };
    },
  },
  [SECTION_IDS.PURCHASE_ORDER]: {
    id: SECTION_IDS.PURCHASE_ORDER,
    label: "Purchase Order",
    icon: "po",
    moleculeKey: "PurchaseOrder",
    // Through Xero with `xero_purchase_orders`, otherwise our own PDF under
    // `purchase_order_generation` — either one shows the section.
    feature: PO_FEATURES,
    isVisible: isSavedSellRequest,
    isComplete: ({ request }) => hasPurchaseOrder(request),
    isLocked: ({ request }) =>
      isSerialVerified(request) ? false : "Verify the serial first",
    getCardData: ({ request }) => ({
      fields: request?.xero_purchase_order_id
        ? [{ label: "PO ID", value: request.xero_purchase_order_id }]
        : request?.purchase_order_number
          ? [{ label: "PO", value: request.purchase_order_number }]
          : [],
    }),
  },
  [SECTION_IDS.PUBLISH_LISTING]: {
    id: SECTION_IDS.PUBLISH_LISTING,
    label: "Publish Listing",
    icon: "publish",
    moleculeKey: "PublishListing",
    isVisible: isSavedSellRequest,
    isComplete: ({ request }) => !!request?.last_quote?.listing_id,
    isLocked: ({ request }) =>
      hasPurchaseOrder(request) ? false : "Generate purchase order first",
    getCardData: ({ request }) => ({
      fields: request?.last_quote?.listing_id
        ? [{ label: "Listing ID", value: request.last_quote.listing_id }]
        : [],
    }),
  },
  // [Removed ASSIGN_SELL_REQUESTS section.]
  // Assigning existing sell requests as PX trade-ins is now handled by
  // the unified SELECT SELL SOURCE chooser ("Existing sell request"
  // tab) inside the dynamic SELECT SELL REQUEST (m/n) sections. The
  // standalone section is no longer needed — dropped from
  // SECTION_ORDER so it never renders.
  _removed_assign_sell_requests: {
    id: SECTION_IDS.ASSIGN_SELL_REQUESTS,
    label: "Assign Sell Requests",
    icon: "assign",
    moleculeKey: "AssignSellRequests",
    isVisible: () => false,
    isComplete: ({ sellRequests }) => (sellRequests?.length || 0) > 0,
    isLocked: () => false,
    getCardData: () => ({ fields: [] }),
  },
  [SECTION_IDS.PX_SUMMARY]: {
    id: SECTION_IDS.PX_SUMMARY,
    label: "PX Summary",
    icon: "pxSummary",
    moleculeKey: "PxSummary",
    isVisible: ({ isPxFlow, sellRequests }) =>
      !!isPxFlow || (sellRequests?.length || 0) > 0,
    isComplete: ({ sellRequests }) => (sellRequests?.length || 0) > 0,
    isLocked: () => false,
    getCardData: ({
      sellRequests,
      request,
      listings,
      toPay,
      quotes,
      listingRate,
      fxMissing,
      fxUnavailable,
    }) => {
      const sellTotal = (sellRequests ?? []).reduce((acc, sr) => {
        // Prefer the quote admin checkmarked via CHANGE QUOTE
        // (state.quotes[sr.id]) over the persisted last_quote — that
        // selection map updates as admin tweaks quote prices and is
        // the authoritative source for Sell Quotes Total. Falls back
        // to last_quote when no explicit selection exists.
        const selected = quotes?.[sr.id];
        const price = Number(
          selected?.price ?? sr.last_quote?.price ?? 0
        );
        return acc + price;
      }, 0);
      const listingPrice = (listings ?? []).reduce(
        (acc, l) => acc + Number(l.price ?? 0),
        0
      );
      // The net the client settles is a cross-currency figure (sells and
      // listing may differ), so it must come from an already-converted
      // source. `toPay` (om.toPay) is exactly listingsPrice − sellRequests,
      // both converted into the order/deal currency, so prefer it whenever
      // it's available. Only fall back to the RAW `listingPrice − sellTotal`
      // (which mixes currencies) when toPay isn't in scope — for a GBP deal
      // the two are identical, so this is byte-identical with the flag off.
      const net =
        typeof toPay === "number"
          ? toPay
          : sellTotal > 0
            ? listingPrice - sellTotal
            : listingPrice;
      // The two line items below (Sell Quotes Total / Buy Listing Price) are
      // shown RAW, each labelled with its own record's currency symbol; only
      // the aggregate `net` is currency-normalised (via toPay, in the order/
      // deal currency). null/absent currency = legacy GBP.
      const sellCurrency =
        (sellRequests ?? [])
          .map((sr) => (quotes?.[sr.id] ?? sr.last_quote)?.currency)
          .find(Boolean) ?? "gbp";
      // Each listing's own price currency (null/absent ⇒ legacy GBP). It is NOT
      // the deal currency: a listing price and the quote that sold it can be in
      // different currencies (order 87362 — £23,500 listing, €26,850 quote).
      const listingCurrency =
        (listings ?? []).map((l) => l.currency).find(Boolean) ?? "gbp";
      // `net` comes from om.toPay, which is denominated in om's deal currency —
      // label it with that, never with a line item's own currency.
      const netCurrency =
        typeof toPay === "number" ? (listingRate ?? listingCurrency) : listingCurrency;
      const fields = [];
      if (sellTotal) {
        fields.push({ label: "Sell Quotes Total", value: `${symbolFor(sellCurrency)}${(sellTotal / 100).toLocaleString()}` });
      }
      if (listingPrice) {
        fields.push({ label: "Buy Listing Price", value: `${symbolFor(listingCurrency)}${(listingPrice / 100).toLocaleString()}` });
      }
      // FX RULE (hooks/fx.js): om had no rate, so toPay is withheld; the raw
      // fallback above would add two currencies 1:1, so don't show it either.
      if (fxMissing) {
        fields.push({
          label: "Client to Pay",
          value: fxUnavailable ? "Rate unavailable" : "…",
        });
      } else if (net !== 0 && !isNaN(net)) {
        fields.push({
          label: net >= 0 ? "Client to Pay" : "Store to Pay",
          value: `${symbolFor(netCurrency)}${(Math.abs(net) / 100).toLocaleString()}`,
        });
      }
      return { fields };
    },
  },
  [SECTION_IDS.PAYMENTS]: {
    id: SECTION_IDS.PAYMENTS,
    label: "Payments",
    icon: "payments",
    moleculeKey: "Payments",
    // Hide entirely until a listing is linked — the user can't act on
    // payments before that, so the empty/locked stub is noise.
    //
    // Source this from the ORDER, not just the request/quote pointer.
    // The request-level link (last_quote.listing_id / linked_listing_id)
    // can drift to null — e.g. a re-quote creates a fresh last_quote
    // without carrying listing_id forward — while the order itself still
    // holds the listing (order.items[].listing_id). The amounts already
    // resolve off the order (seededListings prefers order.items, so toPay
    // stays correct), so once an order has a listing-bearing item Payments
    // must remain visible regardless of pointer drift.
    isVisible: (state) => {
      if (!isOrderOrPx(state)) return false;
      const r = state?.request;
      const hasPointer = !!(r?.last_quote?.listing_id || r?.linked_listing_id);
      const orderHasListing = (state?.order?.items ?? []).some(
        (i) => i?.listing_id
      );
      return hasPointer || orderHasListing;
    },
    isComplete: ({ order }) => order?.status === "paid",
    isLocked: () => false,
    // Dead code — nothing consumes getCardData any more; the smart card's
    // Payments section owns the Deposit / Total Paid / Balance Due rows (in
    // the deal currency, with the Q9 base bracket).
    getCardData: () => ({ fields: [] }),
  },
  [SECTION_IDS.INVOICE]: {
    id: SECTION_IDS.INVOICE,
    label: "Invoice",
    icon: "invoice",
    moleculeKey: "Invoice",
    // Hide until the cart Order exists (created by Payments save).
    isVisible: (state) => isOrderOrPx(state) && !!state?.order?.id,
    isComplete: ({ order }) => hasInvoice(order),
    isLocked: () => false,
    getCardData: ({ order, getPdf }) => {
      const fields = [];
      if (hasInvoice(order)) {
        fields.push({ label: "Invoice", value: order.xero_invoice_number || order.invoice_number });
      }
      if (getPdf) {
        fields.push({ label: "PDF", value: "Generated" });
      }
      return { fields };
    },
  },
  [SECTION_IDS.COMMISSIONS]: {
    id: SECTION_IDS.COMMISSIONS,
    label: "Commissions",
    icon: "commissions",
    moleculeKey: "Commissions",
    // Hide until the invoice has been generated.
    // Staging bypass: Xero is disconnected on staging so the invoice
    // number never lands — unlock once the cart Order exists.
    isVisible: (state) =>
      isOrderOrPx(state) &&
      (isStagingEnv()
        ? !!state?.order?.id
        : !!(hasInvoice(state?.order) || state?.getPdf)),
    isComplete: ({ order }) => Array.isArray(order?.items) && order.items.length > 0,
    isLocked: () => false,
    getCardData: ({ commissionsAmount, gbpToBaseRate }) => {
      const fields = [];
      // gbpToBaseRate null = no known GBP -> base rate (exactly 1 for a GBP
      // base): never show the GBP figure under the base symbol.
      if (
        typeof commissionsAmount === "number" &&
        commissionsAmount > 0 &&
        gbpToBaseRate == null
      ) {
        fields.push({
          label: "Commissions Total",
          value: "Rate unavailable",
          note: "Subtracted from profit",
        });
      } else if (typeof commissionsAmount === "number" && commissionsAmount > 0) {
        // commissionsAmount is the GBP pivot — convert to the merchant's base
        // currency for display (×1 for a GBP base).
        const inBase = commissionsAmount * (gbpToBaseRate || 1);
        fields.push({
          label: "Commissions Total",
          value: `${baseSymbol()}${(inBase / 100).toLocaleString()}`,
          note: "Subtracted from profit",
        });
      }
      return { fields };
    },
  },
  [SECTION_IDS.INTERNAL_DETAILS]: {
    id: SECTION_IDS.INTERNAL_DETAILS,
    label: "Internal Details & Costs",
    icon: "costs",
    moleculeKey: "InternalDetailsAndCosts",
    // Show as soon as there is a LISTING to record against — which is what this
    // section actually writes to. It used to demand an invoice number, and that
    // is the whole reason it sat at the end of the flow: the gate, not the data.
    //
    // A linked listing means a quote has been turned into one, i.e. the rep has
    // passed SELECT ORDER QUOTE ("Listing created for this quote" is one of that
    // section's own checklist items). Read from BOTH the request pointer and the
    // Order's items for the same reason PAYMENTS does: a re-quote can leave
    // `last_quote.listing_id` null while the Order still holds the listing.
    //
    // The staging bypass is gone with the invoice requirement — there is nothing
    // Xero-dependent left to bypass.
    isVisible: (state) => {
      if (!isOrderOrPx(state)) return false;
      const r = state?.request;
      const hasPointer = !!(r?.last_quote?.listing_id || r?.linked_listing_id);
      const orderHasListing = (state?.order?.items ?? []).some((i) => i?.listing_id);
      return hasPointer || orderHasListing;
    },
    // Finalising writes `orders.costs_finalised`, so it can only complete once
    // the Order exists — i.e. after PAYMENTS. Before that the section is
    // reachable and saveable (onto the listing) but not finalisable, which is
    // exactly the truth: there is no Order to stamp yet.
    isComplete: ({ order }) => !!order?.costs_finalised,
    isLocked: () => false,
    // Dead code — nothing consumes getCardData any more (no call site anywhere
    // in src/; RequestSmartCard imports only SECTION_IDS + getSectionStatus).
    // The smart card owns the Buying Cost / Other Costs / Net Profit rows and
    // renders the cost figures from each listing's ORIGINAL amount + currency
    // (resolveOriginalMoney + crossRate).
    //
    // The block that used to live here rendered `gbp_* × gbpToBaseRate`: the
    // backend writes those pivots as original × rate(orig→GBP), so scaling them
    // into a display currency is a SECOND conversion — a cost typed as 1000 USD
    // came back out as 993. It's deleted rather than repaired so it can't be
    // copied into a live surface; stubbed like the other dead entries above.
    getCardData: () => ({ fields: [] }),
  },
  [SECTION_IDS.TRACKING_NUMBER]: {
    id: SECTION_IDS.TRACKING_NUMBER,
    label: "Tracking Number",
    icon: "tracking",
    moleculeKey: "TrackingNumber",
    // Hide until Internal Details & Costs has been finalised.
    // Staging bypass: surface the section once the cart Order exists so
    // reps can walk the full flow without depending on costs being
    // finalised against a real Xero-backed invoice.
    isVisible: (state) =>
      isOrderOrPx(state) &&
      (isStagingEnv() ? !!state?.order?.id : !!state?.order?.costs_finalised),
    isComplete: ({ order }) => !!order?.tracking_number,
    isLocked: () => false,
    getCardData: ({ order }) => {
      if (!order?.tracking_number) return { fields: [] };
      return {
        fields: [{ label: "Tracking", value: order.tracking_number }],
      };
    },
  },
};

// Dynamic "Select Sell Request (m/n)" + "Confirm Sell Request (m/n)"
// section pairs driven by pxProducts / journey state — these appear as
// soon as admin clicks ADD PART EXCHANGE in CONFIRM REQUEST and grow
// with ADD MORE PARTS TO SELL. Mirrors V1's per-sell SEARCH +
// CONFIRM_REQUEST journey steps so admin can set product + condition
// / year / price expectation per trade-in item.
// Dynamic SEARCH + CONFIRM_REQUEST sections for the 2nd and 3rd MAIN
// order products when the rep is building a multi-product deal
// (request group, up to 3 main products). The first/primary main
// uses the static SEARCH + CONFIRM_REQUEST sections from SECTIONS;
// these are the additional ones, indexed by mainSlotIdx starting at 1.
//
// State sources (both counted so a freshly inserted slot renders
// before its product is picked):
//  - additionalMainProducts.length — set after the rep picks a product
//  - journey entries with mainSlotIdx > 0 — set by confirmRequest when
//    ADD_MAIN action fires (the immediate signal to render the slot).
function getDynamicMainSelectSections(state) {
  if (!isOrderOrPx(state)) return [];

  const additionalProducts = state?.additionalMainProducts ?? [];
  const journeyMainSlots = (state?.journey ?? []).filter(
    (j) =>
      j?.component === "search" &&
      j?.mainSlotIdx != null &&
      j?.mainSlotIdx > 0
  );
  // The highest INDEX among journey entries, not their count. On an EDIT
  // deal the hydrated slots live only in state (no journey entries), so a
  // retro-added slot is numbered past them — counting entries would size the
  // list too small and the new slot's section would never render. On the
  // create flow entries run 1..n, so count and max agree.
  const journeyMaxMainSlot = journeyMainSlots.reduce(
    (mx, j) => Math.max(mx, j.mainSlotIdx),
    0
  );
  const total = Math.max(additionalProducts.length, journeyMaxMainSlot);
  if (total === 0) return [];

  const out = [];
  // total + 1 because the primary main (slot 0) is included in the
  // count for the labels — so a "2 additional mains" deal labels as
  // "Add Order Product (2/3)" / "(3/3)" for slots 1 and 2.
  for (let i = 0; i < total; i++) {
    const mainSlotIdx = i + 1; // 1-based: slot 1 and slot 2 of the group
    const totalLabel = total + 1;
    // The tab this slot should open on, recorded on the journey entry when the
    // slot was created ("Add Listing" → Active Listings, "Source Product" →
    // Database). Falls back to the flow's own default in V2SearchProduct.
    const slotDefaultSource =
      journeyMainSlots.find((j) => j.mainSlotIdx === mainSlotIdx)?.defaultSource ??
      null;
    out.push({
      id: `select_main_${mainSlotIdx}`,
      label: `Add Order Product (${mainSlotIdx + 1}/${totalLabel})`,
      navLabel: `Add Order Product (${mainSlotIdx + 1}/${totalLabel})`,
      icon: "assign",
      moleculeKey: "SearchProduct",
      mainSlotIdx,
      defaultSource: slotDefaultSource,
      isVisible: () => true,
      isComplete: () => i < additionalProducts.length,
      isLocked: () => false,
      getCardData: () => ({ fields: [] }),
    });
    out.push({
      id: `confirm_main_${mainSlotIdx}`,
      label: `Confirm Order Product (${mainSlotIdx + 1}/${totalLabel})`,
      navLabel: `Confirm Order Product (${mainSlotIdx + 1}/${totalLabel})`,
      isSubItem: true,
      icon: "generic",
      moleculeKey: "ConfirmRequest",
      mainSlotIdx,
      // The slot's own CONFIRM section is where the rep can take this product
      // out of the deal. Only the ADDITIONAL slots carry it — slot 0 is the
      // singular primary state and cannot be removed (the backend refuses it
      // too), and one control per slot beats repeating it on every section.
      detachable: true,
      isVisible: () => i < additionalProducts.length,
      isComplete: () =>
        !!state?.additionalMainRequests?.[i]?.id &&
        state.additionalMainRequests[i].status !== "draft",
      isLocked: () =>
        i < additionalProducts.length ? false : "Pick the product first",
      getCardData: () => ({ fields: [] }),
    });
    // Per-slot Shopping Cart — visible when the slot's request has a
    // quote OR a linked listing (same gate as the static primary
    // section). Lets the rep link / create a listing for ANY main
    // slot, not just the primary. Closes the gap where a Database-
    // picked slot 2 / slot 3 had no UI surface to attach a listing
    // before SEND QUOTE auto-linked one.
    out.push({
      id: `connect_listing_main_${mainSlotIdx}`,
      label: `Shopping Cart (${mainSlotIdx + 1}/${totalLabel})`,
      navLabel: `Shopping Cart (${mainSlotIdx + 1}/${totalLabel})`,
      isSubItem: true,
      icon: "link",
      moleculeKey: "ConnectListing",
      mainSlotIdx,
      isVisible: () => {
        const sr = state?.additionalMainRequests?.[i];
        return !!sr?.last_quote?.id || !!sr?.linked_listing_id;
      },
      isComplete: () => {
        const sr = state?.additionalMainRequests?.[i];
        return !!(sr?.last_quote?.listing_id || sr?.linked_listing_id);
      },
      isLocked: () => {
        const sr = state?.additionalMainRequests?.[i];
        if (sr?.last_quote?.id) return false;
        if (sr?.linked_listing_id) return false;
        return "Create a quote first";
      },
      getCardData: () => ({ fields: [] }),
    });
    // SEND_QUOTE sections hoisted to the end (see
    // getDynamicSendQuoteSections below).
  }
  return out;
}

// Dynamic SELECT QUOTE sections — one per main slot (primary + each
// additional main) — grouped at the END of the section list after
// every CONFIRM (Order or Sell). Lets the rep finish picking all
// products + part-exchanges first, then walks the quotes in one
// uninterrupted block. SAVE/NEXT chains through Quote 1/m → 2/m →
// 3/m → PX SUMMARY or PAYMENTS.
function getDynamicSendQuoteSections(state) {
  if (!isOrderOrPx(state)) return [];

  const additionalProducts = state?.additionalMainProducts ?? [];
  const journeyMainSlots = (state?.journey ?? []).filter(
    (j) =>
      j?.component === "search" &&
      j?.mainSlotIdx != null &&
      j?.mainSlotIdx > 0
  );
  const totalAdditional = Math.max(
    additionalProducts.length,
    journeyMainSlots.length
  );

  // No additional mains → fall through to the static SEND_QUOTE
  // (which already operates on the primary). No need to emit
  // dynamic per-slot sections in that case.
  if (totalAdditional === 0) return [];

  const totalLabel = totalAdditional + 1; // primary + additional mains
  const out = [];
  for (let i = 0; i < totalAdditional; i++) {
    const mainSlotIdx = i + 1;
    out.push({
      id: `send_quote_main_${mainSlotIdx}`,
      // Counter only when the deal really has more than one order quote — see
      // orderQuoteSlotCount.
      label:
        orderQuoteSlotCount(state) < 2
          ? orderQuoteLabel(state)
          : `${orderQuoteLabel(state)} (${mainSlotIdx + 1}/${totalLabel})`,
      navLabel:
        orderQuoteSlotCount(state) < 2
          ? orderQuoteLabel(state)
          : `${orderQuoteLabel(state)} (${mainSlotIdx + 1}/${totalLabel})`,
      icon: "sendQuote",
      moleculeKey: "SendQuote",
      mainSlotIdx,
      isVisible: (state) =>
        !!state?.user?.is_admin &&
        i < additionalProducts.length &&
        // Same rule per slot: an Active-Listings pick needs no quote.
        !isMainSlotListingSourced(state, mainSlotIdx),
      isComplete: () => {
        const sr = state?.additionalMainRequests?.[i];
        return (
          !!sr?.last_quote?.quote_send_type ||
          !!sr?.last_quote?.listing_id ||
          !!sr?.linked_listing_id
        );
      },
      isLocked: () =>
        state?.additionalMainRequests?.[i]?.id
          ? false
          : "Confirm the product first",
      getCardData: () => ({ fields: [] }),
    });
  }
  return out;
}

function getDynamicPxSelectSections(state) {
  // Trade-ins (part-exchange) only apply to order / px-order /
  // px-cart flows. A SELL request is itself one of the items being
  // traded in — it has no nested trade-in sections of its own.
  if (!isOrderOrPx(state)) return [];

  const pxProducts = state?.pxProducts ?? [];
  const journeyPxSlots = (state?.journey ?? []).filter(
    (j) =>
      j?.component === "search" &&
      j?.pxSellIdx !== null &&
      j?.pxSellIdx !== undefined
  );
  // Highest index + 1, not entry count — see the main-slot generator above.
  // pxSellIdx is 0-based, so a lone hydrated-then-added slot 2 needs 3 rows.
  const journeyMaxPxSlot = journeyPxSlots.reduce(
    (mx, j) => Math.max(mx, j.pxSellIdx + 1),
    0
  );
  const total = Math.max(pxProducts.length, journeyMaxPxSlot);
  if (total === 0) return [];

  const sellRequests = state?.sellRequests ?? [];

  const out = [];
  for (let i = 0; i < total; i++) {
    out.push({
      id: `select_sell_${i}`,
      label: `Select Sell Request (${i + 1}/${total})`,
      navLabel: `Select Sell Request`,
      icon: "assign",
      moleculeKey: "SearchProduct",
      pxSellIdx: i,
      isVisible: () => true,
      isComplete: () => i < pxProducts.length,
      isLocked: () => false,
      getCardData: () => ({ fields: [] }),
    });
    out.push({
      id: `confirm_sell_${i}`,
      label: `Confirm Sell Request (${i + 1}/${total})`,
      navLabel: `Confirm Sell Request`,
      isSubItem: true,
      icon: "generic",
      moleculeKey: "ConfirmRequest",
      pxSellIdx: i,
      // Every trade-in slot can be taken out of the deal — they all live in the
      // pxProducts / pxRequests arrays, so none of them is the singular primary.
      detachable: true,
      // Visible once admin has picked a product for this sell slot —
      // before that there's nothing to confirm.
      isVisible: () => i < pxProducts.length,
      isComplete: () => !!sellRequests[i]?.id,
      isLocked: () =>
        i < pxProducts.length ? false : "Select the sell product first",
      getCardData: () => ({ fields: [] }),
    });
  }
  return out;
}

/**
 * The id a sell-side section has for a given PX slot.
 *
 * The per-trade-in copies are suffixed (`purchasing_agreement_sell_0`), so a
 * molecule that hardcodes the STATIC id in `navigateTo` misses on a PX deal —
 * and a missed target is silently reverted to `visibleSections[0]`, bouncing the
 * rep back to SEARCH PRODUCT. Molecules resolve their target through this with
 * `step.pxSellIdx`, which is null outside a PX slot and then yields the static id.
 */
export function pxSellSectionId(baseId, pxSellIdx) {
  if (pxSellIdx === null || pxSellIdx === undefined) return baseId;
  return `${baseId}_sell_${pxSellIdx}`;
}

/**
 * The sell-side completion sections, one set PER SELL REQUEST in a PX group.
 *
 * On a PX order the money and paperwork for each trade-in belong to that
 * trade-in's own sell Request, not to the parent order. The static sell sections
 * (`SECTIONS[VERIFY_SERIAL]` etc.) are rendered with pxSellIdx = null, so they
 * resolve to the PRIMARY request via getContext — on a PX order that is the
 * ORDER request, and using them would generate a purchasing agreement against
 * the wrong record and stamp `purchasing_agreement_sent_at` on the order row.
 *
 * So these are per-slot instances carrying `pxSellIdx`, exactly like the
 * existing `confirm_sell_{i}` sections. getContext(idx, step) then resolves
 * pxRequests[i], and every molecule below operates on the right request with no
 * change of its own.
 *
 * Verify Serial is included even though the spec lists only the four documents:
 * the agreements and the PO are locked behind `isSerialVerified`, so without it
 * the rep would face four permanently locked sections.
 *
 * CONSIGNMENT AGREEMENT is deliberately NOT emitted here. A trade-in taken
 * against an order is a purchase, so the purchasing agreement is the document
 * the rep needs; the consignment one is still emitted for a standalone sell
 * request by SECTIONS[CONSIGNMENT_AGREEMENT]. There is no per-request
 * purchase-vs-consignment discriminator to gate on — `quote_send_type` offers
 * only SP_CONSIGN today and `requests` has no consignment column — so this is
 * an unconditional flow decision, not a data condition.
 *
 * Positioned after INVOICE by getVisibleSections (see the pxSellExtras splice).
 */
function getDynamicPxSellExtraSections(state) {
  // Gated on the DATA, not on a flow variant: "in case of PX orders" is true of
  // the deal, not of the route it was started from. PX-ness is already
  // data-driven elsewhere (RequestTemplateV2 derives isPxFlow from
  // request/order.px_request_id, not the URL), and a variant flow hands its URL
  // back to the px-order contract the moment a trade-in is added — so keying off
  // the variant would make these sections vanish on reload.
  if (!isOrderOrPx(state)) return [];
  if (!state?.isPxFlow) return [];

  const sellRequests = state?.sellRequests ?? [];
  const pxProducts = state?.pxProducts ?? [];
  const total = Math.max(sellRequests.length, pxProducts.length);
  if (total === 0) return [];

  // Per-slot request, so each section's gates read its OWN record.
  const reqAt = (i) => sellRequests[i] ?? null;
  const multi = total > 1;
  const suffix = (i) => (multi ? ` (${i + 1}/${total})` : "");

  const out = [];
  for (let i = 0; i < total; i++) {
    const stamp = (id, label, icon, moleculeKey, extra) => ({
      id: pxSellSectionId(id, i),
      label: `${label}${suffix(i)}`,
      navLabel: `${label}${suffix(i)}`,
      isSubItem: true,
      icon,
      moleculeKey,
      pxSellIdx: i,
      isVisible: () => !!reqAt(i)?.id,
      getCardData: () => ({ fields: [] }),
      ...extra,
    });

    out.push(
      stamp("verify_serial", "Verify Serial", "serial", "VerifySerial", {
        isComplete: () => isSerialVerified(reqAt(i)),
        isLocked: () => false,
      })
    );
    out.push(
      stamp("purchasing_agreement", "Purchasing Agreement", "doc", "PurchasingAgreement", {
        feature: "purchase_consignment_agreements",
        isComplete: () => !!reqAt(i)?.purchasing_agreement_sent_at,
        isLocked: () =>
          isSerialVerified(reqAt(i)) ? false : "Verify the serial first",
      })
    );
    out.push(
      stamp("sell_payments", "Sell Payments", "payments", "SellPayments", {
        isComplete: () => !!reqAt(i)?.last_quote?.price,
        isLocked: () => false,
      })
    );
    out.push(
      stamp("purchase_order", "Purchase Order", "po", "PurchaseOrder", {
        feature: PO_FEATURES,
        isComplete: () => hasPurchaseOrder(reqAt(i)),
        isLocked: () =>
          isSerialVerified(reqAt(i)) ? false : "Verify the serial first",
      })
    );
    out.push(
      stamp("publish_listing", "Publish Listing", "publish", "PublishListing", {
        isComplete: () => !!reqAt(i)?.last_quote?.listing_id,
        isLocked: () =>
          hasPurchaseOrder(reqAt(i))
            ? false
            : "Generate purchase order first",
      })
    );
  }
  return out;
}

// Helper to compute the visible / ordered section list for the current
// request state. Returns array of section entries (visible ones only).
// Dynamic SELECT SELL REQUEST sections are interleaved after
// CONFIRM_REQUEST and before ASSIGN_SELL_REQUESTS.
/**
 * Apply a purpose-built flow variant to the composed section list.
 *
 * Deliberately the LAST step of getVisibleSections, and deliberately only
 * capable of three things — dropping sections, renaming them, and hoisting a few
 * to the front:
 *
 *  - autoSkip(state) → ids to remove. Removal is the only true "skip" in this
 *    architecture: `onAdvance` walks the visible list, so a removed section is
 *    stepped over, and the left rail never renders a tick for a section the rep
 *    was never shown. (A section that is merely navigated past stays "pending"
 *    in the rail, which reads as unfinished work.)
 *  - sectionLabels → per-id label override, applied to BOTH `label` (accordion)
 *    and `navLabel` (left rail) so the two can never disagree.
 *  - sectionFirst → ids hoisted to the front in the order given. The variants use
 *    it for ASSIGN CLIENT: on these flows the rep has the client in front of them
 *    before they know the product, and CONFIRM REQUEST is locked until a client
 *    exists anyway.
 *
 * It intentionally cannot ADD sections: everything the variants need already
 * exists in SECTIONS, gated by isVisible. Keeping additions out of here means
 * a variant can't invent a section with no molecule behind it.
 */
function applyFlowVariant(sections, state) {
  const variant = state?.flowVariant;
  if (!variant) return sections;

  const skipped = new Set(variant.autoSkip?.(state) ?? []);
  const labels = variant.sectionLabels ?? {};

  const relabelled = sections
    .filter((s) => !skipped.has(s.id))
    .map((s) => {
      const override = labels[s.id];
      if (!override) return s;
      // Never clobber a dynamically numbered label ("(2/3)") — those carry
      // slot information the rep needs more than the variant's wording.
      if (/\(\d+\/\d+\)/.test(s.label)) return s;
      return { ...s, label: override, navLabel: override };
    });

  // `sectionFirst` hoists ids to the front, in the order given; everything else
  // keeps its SECTION_ORDER position. A HOIST rather than a full order override
  // on purpose: the dynamic multi-slot sections are positioned relative to
  // CONFIRM_REQUEST by the caller above, and a variant restating the whole order
  // would silently fight that. Matching is by exact id, so only the primary
  // sections move — the dynamic ones are suffixed (`select_main_1`,
  // `confirm_sell_0`, …).
  //
  // Safe for the pair that actually needs it: neither SEARCH_PRODUCT nor
  // ASSIGN_CLIENT is locked by the other (`isLocked: () => false` on both), and
  // CONFIRM_REQUEST is locked until a client exists ("Assign a client first") —
  // so client-first is the order that section was already asking for.
  const hoist = variant.sectionFirst ?? [];
  if (hoist.length === 0) return relabelled;

  const rank = new Map(hoist.map((id, i) => [id, i]));
  const front = [];
  const rest = [];
  relabelled.forEach((s) => (rank.has(s.id) ? front.push(s) : rest.push(s)));
  front.sort((a, b) => rank.get(a.id) - rank.get(b.id));

  return [...front, ...rest];
}

/**
 * SELECT QUOTE, once per part-exchange sell request.
 *
 * The order side has always had its own quote sections (primary SEND_QUOTE +
 * per-main `send_quote_main_n`); the sell side had none, so the only way to pick
 * a trade-in's quote was the CHANGE QUOTE popup on PX SUMMARY. These give each
 * sell request the same first-class step, and PX SUMMARY becomes the review at
 * the end of the chain rather than a place to go and do the quoting.
 *
 * Gated on the DATA (a PX deal with sell requests), matching
 * getDynamicPxSellExtraSections — a variant flow hands its URL back to the
 * px-order contract as soon as a trade-in is added, so keying off the flow
 * variant would make these vanish on reload.
 *
 * Admin-only, like the order-side quote sections: quoting is not a client action.
 */
function getDynamicSellQuoteSections(state) {
  if (!isOrderOrPx(state)) return [];
  // `isPxFlow` is supplied by RequestTemplateV2 (derived from
  // request/order.px_request_id), same as getDynamicPxSellExtraSections.
  if (!state?.isPxFlow) return [];

  const sellRequests = (state?.sellRequests ?? []).filter(Boolean);
  const total = sellRequests.length;
  if (total === 0) return [];

  const quotes = state?.quotes ?? {};
  const out = [];
  for (let i = 0; i < total; i++) {
    const sellReq = sellRequests[i];
    // Numbered only when there is more than one trade-in — the same rule the
    // sell-side completion sections use.
    const suffix = total > 1 ? ` (${i + 1}/${total})` : "";
    out.push({
      id: `send_quote_sell_${i}`,
      label: `Select Sell Quote${suffix}`,
      // Shorter in the rail: "Select Sell Quote (1/2)" truncates mid-counter in
      // that column, and the counter is the part the rep needs.
      navLabel: `Sell Quote${suffix}`,
      icon: "sendQuote",
      moleculeKey: "SendQuote",
      pxSellIdx: i,
      isVisible: ({ user }) => !!user?.is_admin,
      // Complete once a quote is CHOSEN for this sell request — either picked
      // here (the `quotes` map, which is also what the net is computed from) or
      // already sent/accepted on the request itself.
      isComplete: () =>
        !!quotes?.[sellReq?.id] ||
        !!sellReq?.last_quote?.quote_send_type,
      isLocked: () =>
        sellReq?.id ? false : "Confirm this part-exchange product first",
      getCardData: () => ({ fields: [] }),
    });
  }
  return out;
}

/**
 * Place the sell-quote sections immediately after the ORDER's quote block and
 * before PX SUMMARY, which is the order the flow now walks: order quotes → sell
 * quotes → PX SUMMARY → payments. Falls back to inserting before PX_SUMMARY, or
 * appending, so they can never disappear because a quote section was hidden.
 */
function withSellQuotes(sections, sellQuotes) {
  if (sellQuotes.length === 0) return sections;

  let at = -1;
  sections.forEach((s, i) => {
    if (s.id === SECTION_IDS.SEND_QUOTE || String(s.id).startsWith("send_quote_main_")) {
      at = i;
    }
  });
  if (at === -1) {
    const pxIdx = sections.findIndex((s) => s.id === SECTION_IDS.PX_SUMMARY);
    if (pxIdx === -1) return [...sections, ...sellQuotes];
    return [...sections.slice(0, pxIdx), ...sellQuotes, ...sections.slice(pxIdx)];
  }
  return [...sections.slice(0, at + 1), ...sellQuotes, ...sections.slice(at + 1)];
}

/**
 * Insert the per-sell-request completion sections directly after INVOICE, which
 * is where the spec puts them ("additionally shown after Invoice"). Falls back
 * to appending if INVOICE isn't visible yet (no cart Order), so the sections
 * never vanish just because the order hasn't been invoiced.
 */
function withPxSellExtras(sections, extras) {
  if (extras.length === 0) return sections;
  const invoiceIdx = sections.findIndex((s) => s.id === SECTION_IDS.INVOICE);
  if (invoiceIdx === -1) return [...sections, ...extras];
  return [
    ...sections.slice(0, invoiceIdx + 1),
    ...extras,
    ...sections.slice(invoiceIdx + 1),
  ];
}

export function getVisibleSections(state) {
  // Plan gating: a section declaring `feature` is dropped when the store's plan
  // doesn't include it. `state.hasFeature` is supplied by the template from the
  // entitlements context; when absent (non-component callers) every section stays
  // visible, which keeps this fail-open.
  //
  // This has to cover the DYNAMIC groups as well as the static list. It used to
  // apply to `staticSections` only, so the per-slot copies of gated paperwork
  // leaked: a store without the add-on still got `purchasing_agreement_sell_{i}`
  // and its purchase order on a PX deal, while the same sections were correctly
  // hidden on a plain sell request.
  const hasFeature = state?.hasFeature ?? (() => true);
  const gateByPlan = (sections) =>
    sections.filter((s) => !s.feature || hasFeature(s.feature));

  const dynamicMain = gateByPlan(getDynamicMainSelectSections(state));
  const dynamicPx = gateByPlan(getDynamicPxSelectSections(state));
  // Filtered by their own isVisible, like the sell-quote and PX-extra groups.
  // Without this the per-slot rules in getDynamicSendQuoteSections were
  // decorative — a slot sourced from Active Listings still rendered a quote
  // section. dynamicMain / dynamicPx are deliberately left unfiltered: their
  // visibility is already expressed by how many slots exist, and filtering them
  // here would change long-standing behaviour beyond this change.
  const dynamicSendQuote = gateByPlan(getDynamicSendQuoteSections(state)).filter((s) =>
    s.isVisible(state)
  );
  const sellQuotes = gateByPlan(getDynamicSellQuoteSections(state)).filter((s) =>
    s.isVisible(state)
  );
  const pxSellExtras = gateByPlan(getDynamicPxSellExtraSections(state)).filter((s) =>
    s.isVisible(state)
  );

  // Inject (1/m) suffix on the primary SEARCH/CONFIRM/SEND_QUOTE
  // labels when the group has additional main slots. Done by
  // wrapping each static section with a label override before
  // visibility filtering so the rep sees a consistent counter
  // across all slots, including the primary.
  const totalMains =
    1 +
    Math.max(
      (state?.additionalMainProducts ?? []).length,
      (state?.journey ?? []).filter(
        (j) =>
          j?.component === "search" &&
          j?.mainSlotIdx != null &&
          j?.mainSlotIdx > 0
      ).length
    );

  const labelPrimaryWithCounter = (s) => {
    // The order-side quote is renamed whether or not there is a counter to add,
    // so this case sits ABOVE the single-main early return below.
    if (s.id === SECTION_IDS.SEND_QUOTE) {
      const base = orderQuoteLabel(state);
      // Counter only when the deal really has more than one order quote.
      const text =
        totalMains <= 1 || orderQuoteSlotCount(state) < 2
          ? base
          : `${base} (1/${totalMains})`;
      return { ...s, label: text, navLabel: text };
    }
    if (totalMains <= 1) return s;
    if (s.id === SECTION_IDS.SEARCH_PRODUCT) {
      return {
        ...s,
        label: `Search Order Product (1/${totalMains})`,
        navLabel: `Search Order Product (1/${totalMains})`,
      };
    }
    if (s.id === SECTION_IDS.CONFIRM_REQUEST) {
      return {
        ...s,
        label: `Confirm Order Product (1/${totalMains})`,
        navLabel: `Confirm Order Product (1/${totalMains})`,
      };
    }
    if (s.id === SECTION_IDS.CONNECT_LISTING) {
      return {
        ...s,
        label: `Shopping Cart (1/${totalMains})`,
        navLabel: `Shopping Cart (1/${totalMains})`,
      };
    }
    return s;
  };

  const staticSections = gateByPlan(SECTION_ORDER.map((id) => SECTIONS[id]))
    .filter((s) => s.isVisible(state))
    .map(labelPrimaryWithCounter);

  if (
    dynamicMain.length === 0 &&
    dynamicPx.length === 0 &&
    dynamicSendQuote.length === 0
  ) {
    return applyFlowVariant(
      withPxSellExtras(withSellQuotes(staticSections, sellQuotes), pxSellExtras),
      state
    );
  }

  // Final order (multi-main / multi-PX):
  //   ... earlier sections ...
  //   primary CONFIRM REQUEST (1/m)
  //   ADD ORDER PRODUCT (2/m) + CONFIRM ORDER PRODUCT (2/m)
  //   ADD ORDER PRODUCT (3/m) + CONFIRM ORDER PRODUCT (3/m)
  //   SELECT SELL REQUEST (1/n) + CONFIRM SELL REQUEST (1/n)
  //   ...
  //   ── all SELECT QUOTE sections grouped here ──
  //   primary SELECT QUOTE (1/m)
  //   SELECT QUOTE (2/m), (3/m)
  //   SELECT SELL QUOTE (1/n), (2/n)  ← one per trade-in
  //   VERIFY_SERIAL / sell stuff
  //   PX_SUMMARY / PAYMENTS / etc.
  //
  // Mechanically: split staticSections at CONFIRM_REQUEST and at
  // SEND_QUOTE. Insert dynamic main + dynamic PX after CONFIRM_REQUEST.
  // Move primary SEND_QUOTE next to the dynamic quotes (since the
  // static index is now bunch of slots earlier).
  const confirmIdx = staticSections.findIndex(
    (s) => s.id === SECTION_IDS.CONFIRM_REQUEST
  );
  const sendQuoteIdx = staticSections.findIndex(
    (s) => s.id === SECTION_IDS.SEND_QUOTE
  );

  if (confirmIdx === -1) {
    return applyFlowVariant(
      withPxSellExtras(
        withSellQuotes(
          [...staticSections, ...dynamicMain, ...dynamicPx, ...dynamicSendQuote],
          sellQuotes
        ),
        pxSellExtras
      ),
      state
    );
  }

  // Pull PX_SUMMARY and the primary SEND_QUOTE out of staticSections
  // so we can re-position them deterministically. Everything else
  // keeps its original SECTION_ORDER position.
  const pxSummaryIdx = staticSections.findIndex(
    (s) => s.id === SECTION_IDS.PX_SUMMARY
  );
  const pxSummary = pxSummaryIdx > -1 ? [staticSections[pxSummaryIdx]] : [];
  const primarySendQuote =
    sendQuoteIdx > -1 ? [staticSections[sendQuoteIdx]] : [];

  // beforeQuote = everything up to and including primary CONFIRM,
  // minus the primary SEND_QUOTE and minus PX_SUMMARY (both
  // re-positioned below).
  const beforeQuote = staticSections
    .slice(0, confirmIdx + 1)
    .filter(
      (s) =>
        s.id !== SECTION_IDS.SEND_QUOTE && s.id !== SECTION_IDS.PX_SUMMARY
    );
  const afterConfirm = staticSections
    .slice(confirmIdx + 1)
    .filter(
      (s) =>
        s.id !== SECTION_IDS.SEND_QUOTE && s.id !== SECTION_IDS.PX_SUMMARY
    );

  // Order rule per UX — PX and non-PX now share ONE order so the flow
  // is linear/uni-directional: the rep quotes the order product(s)
  // first, THEN reviews the whole deal on PX SUMMARY (populated because
  // the order quote now exists), THEN pays:
  //   ... → main + dyn-main → dyn-px (sell requests)
  //       → SELECT QUOTE block (order quotes — primary + per-slot,
  //         kept CONSECUTIVE) → PX_SUMMARY → afterConfirm (PAYMENTS / ...)
  // PX_SUMMARY only renders when there are sell requests (its
  // isVisible), so a plain order effectively goes quotes → payments.
  // Navigation that walks this order (kept in sync here):
  //   - CONFIRM finish / Finalise Part Exchange → "send_quote", i.e. the ORDER's
  //     FIRST quote section (useRequest.confirmRequest)
  //   - last ORDER quote Save → "send_quote_sell_0" when the deal has trade-ins,
  //     else "internal_details" (SendQuote.onSave)
  //   - last SELL quote Save → "px_summary" (SendQuote.onSave)
  //   - PX SUMMARY Proceed → "internal_details" (RequestSectionView onProceed)
  //   - INTERNAL DETAILS Save/Finalise → PAYMENTS (useOrderManagement.saveCosts)
  //   - COMMISSIONS Save → TRACKING NUMBER (useOrderManagement.saveCommissions)
  // Both "internal_details" targets fall back to "payments" through
  // resolveNavTarget when the deal has no linked listing yet.
  return applyFlowVariant(
    withPxSellExtras(
      [
        ...beforeQuote,
        ...dynamicMain,
        ...dynamicPx,
        ...primarySendQuote,
        ...dynamicSendQuote,
        ...sellQuotes,
        ...pxSummary,
        ...afterConfirm,
      ],
      pxSellExtras
    ),
    state
  );
}


/**
 * Resolve a navigation target against the sections a deal actually has.
 *
 * Navigation between sections uses id LITERALS (confirmRequest's
 * { nextSectionId }, SendQuote's hand-overs), and a target can legitimately be
 * absent: an order sourced from Active Listings has no quote section at all.
 * Selecting a missing id makes RequestTemplateV2's stillVisible guard reset the
 * selection to the FIRST section, which reads to the rep as being thrown back to
 * the start of the flow.
 *
 * So walk FORWARD through the chain instead — order quotes → sell quotes → the
 * PX review → payments — and return null when nothing matches, which leaves the
 * rep where they are rather than somewhere arbitrary.
 */
export function resolveNavTarget(sections, targetId) {
  if (!targetId) return null;
  const list = sections ?? [];
  const has = (id) => list.some((s) => s.id === id);
  if (has(targetId)) return targetId;

  const firstWithPrefix = (prefix) =>
    list.find((s) => String(s.id).startsWith(prefix))?.id ?? null;
  const orIfPresent = (id) => (has(id) ? id : null);

  // The canonical successor: the first VISIBLE section after `id` in
  // SECTION_ORDER. The chains above/below are deliberate PREFERENCES (a PX deal
  // wants its summary before payments); this is the backstop for flows whose
  // block after the target simply is not the cart one.
  //
  // Sell requests are the case that exposed the gap. Their post-quote block is
  // VERIFY SERIAL → agreements → SELL PAYMENTS → PO → PUBLISH, none of which
  // appeared in any chain here, so every fallback was an order/cart section a
  // sell request never has. A sell request whose SEND QUOTE is hidden — the
  // product came from a listing, which is exactly what the Previous Purchases
  // tab produces — resolved to nothing at all, and ConfirmRequest's
  // "Continue without" queued a section that could never appear. The button
  // looked dead because nothing moved and nothing errored.
  const nextVisibleAfter = (id) => {
    const from = SECTION_ORDER.indexOf(id);
    if (from < 0) return null;
    for (let i = from + 1; i < SECTION_ORDER.length; i += 1) {
      if (has(SECTION_ORDER[i])) return SECTION_ORDER[i];
    }
    return null;
  };

  // INTERNAL DETAILS & COSTS now sits between the quote block and PAYMENTS, so every chain that
  // used to end at PAYMENTS passes through it first. It can legitimately be absent — it needs a
  // linked listing — which is exactly why these are fallback chains rather than literals.
  const afterQuotes = () =>
    orIfPresent(SECTION_IDS.INTERNAL_DETAILS) ??
    orIfPresent(SECTION_IDS.PAYMENTS) ??
    nextVisibleAfter(SECTION_IDS.SEND_QUOTE);

  if (
    targetId === SECTION_IDS.SEND_QUOTE ||
    String(targetId).startsWith("send_quote_main_")
  ) {
    return (
      firstWithPrefix("send_quote_main_") ??
      firstWithPrefix("send_quote_sell_") ??
      orIfPresent(SECTION_IDS.PX_SUMMARY) ??
      afterQuotes()
    );
  }

  if (String(targetId).startsWith("send_quote_sell_")) {
    return (
      firstWithPrefix("send_quote_sell_") ??
      orIfPresent(SECTION_IDS.PX_SUMMARY) ??
      afterQuotes()
    );
  }

  if (targetId === SECTION_IDS.PX_SUMMARY) return afterQuotes();

  // A deal with no listing linked yet has no INTERNAL DETAILS section, and the rep who was sent
  // there must land on PAYMENTS rather than be bounced to the start of the flow.
  if (targetId === SECTION_IDS.INTERNAL_DETAILS) return orIfPresent(SECTION_IDS.PAYMENTS);

  return null;
}
// Helper to compute per-section status for nav/smart-card rendering.
export function getSectionStatus(section, state) {
  const locked = section.isLocked(state);
  if (locked) {
    return { kind: "locked", message: locked };
  }
  if (section.isComplete(state)) {
    return { kind: "complete" };
  }
  return { kind: "pending" };
}
